import Button from 'tio-ui/components/button';
import Checkbox from 'tio-ui/components/checkbox';
import Input from 'tio-ui/components/input';
import RadioGroup from 'tio-ui/components/radio-group';
import Form from 'tio-ui/components/form';
import Textarea from 'tio-ui/components/textarea';
import ReSelect from 'tio-ui/components/select';
import Divider from 'tio-ui/components/divider';
import { dropTask } from 'ember-concurrency';
import { or } from 'tio-ui/utilities';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import { get } from '@ember/object';
import { Header, Section, VStack, HStack } from 'tio-ui/components/layout';
import {
  PhCheck,
  PhPencilSimple,
  PhArrowSquareOut,
  PhCaretDown,
  PhCaretUp,
} from 'ember-phosphor-icons';
import { service } from '@ember/service';
import { stateOptions } from 'tio-common/components/strategy-finder/strategy-finder-form';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import * as v from 'valibot';
import AlertBanner from 'tio-ui/components/alert-banner';
import Component from '@glimmer/component';
import DescriptionList from 'tio-ui/components/description-list';
import DefinitionList from 'tio-common/components/ui/definition-list';
import Flatpickr from 'tio-common/components/flatpickr';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import inputmask from 'tio-common/modifiers/inputmask';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import SignaturePad from 'tio-common/components/signature-pad';
import Table from 'tio-ui/components/table';
import type { FormResult } from 'tio-ui/components/form';
import type { IntlService } from 'ember-intl';
import type { PointGroup } from 'signature_pad';
import type { EmployeeExtension as Employee } from 'tio-data/data/resources/employee.ext';
import type Owner from '@ember/owner';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import { reloadRecord, saveRecord } from 'tio-data/requests/save-record';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { PslfGroup } from 'tio-data/data/resources/pslf-group.type';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import ApproverOboPastPeriodForm from 'tio-employee/components/pslf/admin/approver-obo-past-period-form';
import type { PastEmploymentPeriod } from 'tio-employee/components/pslf/admin/approver-obo-past-period-form';
import { createOboRequest, type CreateOboResponse } from 'tio-data/requests/pslf-form';
import { rejectPslfFormRequest, signEmployerRequest } from 'tio-data/requests/pslf-form-actions';

export interface AdminPslfFormReviewSignature {
  Args: {
    model: {
      form: PslfFormModel;
      employee: Employee;
      formsAwaitingApproval: PslfFormModel[];
      formsApproved: PslfFormModel[];
    };
  };
  Element: HTMLElement;
}

interface FormModel {
  employmentStartDate: string;
  employmentEndDate: string;
  currentEmployee: boolean;
  weeklyHours: number;
  employmentStatus: string;
  employerContactName: string;
  employerContactTitle: string;
  employerContactPhoneNumber: string;
  employerContactEmail: string;
}

export default class AdminPslfFormReviewRoute extends Component<AdminPslfFormReviewSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare notifications: NotificationsService;

  @tracked formData: FormModel = {
    employmentStartDate: '',
    employmentEndDate: '',
    currentEmployee: true,
    weeklyHours: 0,
    employmentStatus: '',
    employerContactName: '',
    employerContactTitle: '',
    employerContactPhoneNumber: '',
    employerContactEmail: '',
  };

  @tracked employerInfoCorrect = true;
  @tracked showConfirmationModal = false;
  @tracked hasSubmitted = false;
  @tracked signaturePoints: PointGroup[] = [];
  @tracked signatureImage?: string;
  @tracked signatureFieldErrors: string[] = [];
  @tracked isOpenSignatureModal = false;
  @tracked formValidationErrors: Record<string, string[]> = {};
  @tracked selectedGroup?: PslfGroup;

  // OBO state
  @tracked oboPastPeriods: PastEmploymentPeriod[] = [];

  // Approved forms truncation
  @tracked approvedFormsLimit = 3;

  // Rejection modal state
  @tracked showRejectModal = false;
  @tracked rejectComment = '';
  @tracked rejectCommentErrors: string[] = [];

  // Employer resolution for unknown FEIN forms
  @tracked employerResolutionMethod: 'selectGroup' | 'manualEntry' = 'selectGroup';
  @tracked manualEmployerData = {
    employerName: '',
    employerFein: '',
    employerStreetAddress: '',
    employerCity: '',
    employerState: '',
    employerPostalCode: '',
  };

  constructor(owner: Owner, args: AdminPslfFormReviewSignature['Args']) {
    super(owner, args);
    this.setInitialFormData(args.model.form.employerFields, args.model.employee);

    const employerFields = args.model.form.employerFields as Record<string, unknown>;
    if (
      employerFields['unknownEmployerFein'] === true ||
      employerFields['unknown-employer-fein'] === true
    ) {
      this.employerInfoCorrect = false;
    }
  }

  get canShowOboAction() {
    return this.args.model.form.isCurrentEmployer;
  }

  get visibleApprovedForms() {
    return this.args.model.formsApproved.slice(0, this.approvedFormsLimit);
  }

  get hasMoreApprovedForms() {
    return this.approvedFormsLimit < this.args.model.formsApproved.length;
  }

  get canShowLessApprovedForms() {
    return this.approvedFormsLimit > 3;
  }

  showMoreApprovedForms = () => {
    this.approvedFormsLimit += 3;
  };

  showLessApprovedForms = () => {
    this.approvedFormsLimit = 3;
  };

  handleOboPeriodsChange = (periods: PastEmploymentPeriod[]) => {
    this.oboPastPeriods = periods;
  };

  setInitialFormData(fields: Record<string, unknown>, employee: Employee) {
    const employeeAddition = employee.employeeAddition?.processed?.merged;
    const isPriorPeriod = this.args.model.form.isPriorPeriodForm;

    this.formData = {
      currentEmployee: !isPriorPeriod,
      weeklyHours: parseInt((fields['employment-hours'] as string) || '0', 10),
      employmentStartDate: (fields['employment-start-date'] as string) || '',
      employmentEndDate: (fields['employment-end-date'] as string) || '',
      employmentStatus: (fields['employment-status'] as string) || '',
      employerContactName:
        (employeeAddition?.['firstName'] || '') + ' ' + (employeeAddition?.['lastName'] || ''),
      employerContactPhoneNumber:
        (employeeAddition?.['phoneNumber'] as string) || employee.primaryPhoneNumber?.number || '',
      employerContactEmail: (employeeAddition?.['email'] as string) || employee.email || '',
      employerContactTitle: (employeeAddition?.['title'] as string) || '',
    };
  }

  get isPriorPeriodForm() {
    return this.args.model.form.isPriorPeriodForm;
  }

  get hasUnknownEmployerFein(): boolean {
    const employerFields = this.args.model.form.employerFields as Record<string, unknown>;
    return (
      employerFields['unknownEmployerFein'] === true ||
      employerFields['unknown-employer-fein'] === true
    );
  }

  setEmployerResolutionMethod = (method: string) => {
    if (method !== 'selectGroup' && method !== 'manualEntry') {
      return;
    }

    this.employerResolutionMethod = method;
    if (method === 'manualEntry') {
      this.selectedGroup = undefined;
    }
    if (this.hasSubmitted) {
      this.validateForm();
    }
  };

  setManualEmployerField = (field: string, value: string) => {
    this.manualEmployerData = {
      ...this.manualEmployerData,
      [field]: value,
    };
    if (this.hasSubmitted) {
      this.validateForm();
    }
  };

  get showEndDateField() {
    return this.isPriorPeriodForm || !this.formData.currentEmployee;
  }

  onSignatureUpdate = (points: PointGroup[], signatureUrl: string) => {
    this.signaturePoints = points;
    this.signatureImage = signatureUrl;

    if (signatureUrl) {
      this.signatureFieldErrors = [];
    }
  };

  closeSignatureModal = () => {
    this.signatureImage = undefined;
    this.signaturePoints = [];
    this.isOpenSignatureModal = false;
  };

  openSignatureModal = () => {
    this.isOpenSignatureModal = true;
  };

  get borrowerFields() {
    return this.args.model.form.borrowerFields;
  }
  get employerFields() {
    return this.args.model.form.employerFields;
  }

  get formEmployeeName() {
    return this.borrowerFields['borrower-name'];
  }

  get employmentStatusText() {
    return this.formData.employmentStatus === 'fullTime'
      ? this.intl.t('pslf.workflow.full_time_employee')
      : this.intl.t('pslf.workflow.part_time_employee');
  }

  get currentEmployeeText() {
    return this.formData.currentEmployee
      ? this.intl.t('pslf.employer_workflow.current_employee')
      : this.intl.t('pslf.employer_workflow.prior_employee');
  }

  get displayEmployerName() {
    if (!this.employerInfoCorrect) {
      if (this.employerResolutionMethod === 'manualEntry' && this.manualEmployerData.employerName) {
        return this.manualEmployerData.employerName;
      }
      if (this.selectedGroup) {
        return this.selectedGroup.employerLegalName;
      }
    }
    return this.employerFields['employer-name'];
  }

  get displayEmployerFein() {
    if (!this.employerInfoCorrect) {
      if (this.employerResolutionMethod === 'manualEntry' && this.manualEmployerData.employerFein) {
        return this.manualEmployerData.employerFein;
      }
      if (this.selectedGroup) {
        return this.selectedGroup.employerFein;
      }
    }
    return this.employerFields['employer-fein'];
  }

  get displayEmployerAddress() {
    if (!this.employerInfoCorrect) {
      if (
        this.employerResolutionMethod === 'manualEntry' &&
        this.manualEmployerData.employerStreetAddress
      ) {
        const data = this.manualEmployerData;
        const parts = [
          data.employerStreetAddress,
          data.employerCity,
          data.employerState,
          data.employerPostalCode,
        ].filter(Boolean);
        return parts.join(', ');
      }
      if (this.selectedGroup) {
        const group = this.selectedGroup;
        const parts = [
          group.employerAddressLine1,
          group.employerAddressLine2,
          group.employerCity,
          group.employerState,
          group.employerPostalCode,
        ].filter(Boolean);
        return parts.join(', ');
      }
    }
    return this.args.model.form.fullEmployerAddress;
  }

  get formSchema() {
    // Check if manual entry validation is needed
    const needsManualValidation =
      !this.employerInfoCorrect && this.employerResolutionMethod === 'manualEntry';

    const baseSchema = v.object({
      employmentStatus: v.pipe(
        v.string('Employment status is required'),
        v.minLength(1, 'Employment status is required'),
        v.picklist(['fullTime', 'partTime'], 'Employment status must be full-time or part-time')
      ),
      employmentStartDate: v.pipe(
        v.string('Employment start date is required'),
        v.minLength(1, 'Employment start date is required'),
        v.isoDate('Please enter a valid date')
      ),
      weeklyHours: v.pipe(
        v.number('Weekly hours must be a number'),
        v.minValue(1, 'Weekly hours must be at least 1')
      ),
      currentEmployee: v.boolean(),
      employerInfoCorrect: v.pipe(
        v.boolean(),
        v.check((value) => {
          if (value === true) return true;
          // When employer info is not correct, either group must be selected OR manual entry
          if (this.employerResolutionMethod === 'selectGroup') {
            return !!this.selectedGroup;
          }
          // For manual entry, individual field validation will handle errors
          return true;
        }, 'Please select an employer group')
      ),
      // Manual employer fields - only validated when manual entry is selected
      manualEmployerName: needsManualValidation
        ? v.pipe(v.string('Employer name is required'), v.minLength(1, 'Employer name is required'))
        : v.optional(v.string()),
      manualEmployerFein: needsManualValidation
        ? v.pipe(
            v.string('Employer FEIN is required'),
            v.minLength(1, 'Employer FEIN is required'),
            v.regex(/^\d{2}-\d{7}$/, 'FEIN must be in format XX-XXXXXXX')
          )
        : v.optional(v.string()),
      manualEmployerStreetAddress: needsManualValidation
        ? v.pipe(
            v.string('Street address is required'),
            v.minLength(1, 'Street address is required')
          )
        : v.optional(v.string()),
      manualEmployerCity: needsManualValidation
        ? v.pipe(v.string('City is required'), v.minLength(1, 'City is required'))
        : v.optional(v.string()),
      manualEmployerState: needsManualValidation
        ? v.pipe(v.string('State is required'), v.minLength(1, 'State is required'))
        : v.optional(v.string()),
      manualEmployerPostalCode: needsManualValidation
        ? v.pipe(
            v.string('Postal code is required'),
            v.minLength(1, 'Postal code is required'),
            v.regex(/^\d{5}$/, 'Please enter a valid 5-digit postal code')
          )
        : v.optional(v.string()),
      employerContactName: v.pipe(v.string('Name is required'), v.minLength(1, 'Name is required')),
      employerContactTitle: v.pipe(
        v.string('Title is required'),
        v.minLength(1, 'Title is required')
      ),
      employerContactPhoneNumber: v.pipe(
        v.string('Phone number is required'),
        v.minLength(1, 'Phone number is required'),
        v.transform((input) => {
          const digits = input.replace(/\D/g, '');
          if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
          } else if (digits.length === 11 && digits.startsWith('1')) {
            const phoneDigits = digits.slice(1);
            return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(
              6
            )}`;
          }
          return input;
        }),
        v.check((input) => {
          const digits = input.replace(/\D/g, '');
          return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
        }, 'Please enter a valid US phone number.')
      ),
      employerContactEmail: v.pipe(
        v.string('Email is required'),
        v.minLength(1, 'Email is required'),
        v.email('Please enter a valid email address.'),
        v.transform((input) => input.trim().toLowerCase())
      ),
    });

    // Add conditional validation for end date if not current employee or prior period form
    if (this.showEndDateField) {
      return v.object({
        ...baseSchema.entries,
        employmentEndDate: v.pipe(
          v.string('Employment end date is required for former employees'),
          v.minLength(1, 'Employment end date is required for former employees'),
          v.isoDate('Please enter a valid date'),
          v.check(
            (value) =>
              !this.formData.employmentStartDate || value >= this.formData.employmentStartDate,
            'End date must be after start date'
          )
        ),
      });
    }

    return v.object({
      ...baseSchema.entries,
      employmentEndDate: v.optional(v.string()),
    });
  }

  validateForm() {
    const result = v.safeParse(this.formSchema, {
      ...this.formData,
      employerInfoCorrect: this.employerInfoCorrect,
      manualEmployerName: this.manualEmployerData.employerName,
      manualEmployerFein: this.manualEmployerData.employerFein,
      manualEmployerStreetAddress: this.manualEmployerData.employerStreetAddress,
      manualEmployerCity: this.manualEmployerData.employerCity,
      manualEmployerState: this.manualEmployerData.employerState,
      manualEmployerPostalCode: this.manualEmployerData.employerPostalCode,
    });
    if (result.success) {
      this.formValidationErrors = {};
    } else {
      const validationErrors: Record<string, string[]> = {};

      for (const issue of result.issues) {
        const path = issue.path?.[0]?.key as string;
        if (path) {
          if (!validationErrors[path]) {
            validationErrors[path] = [];
          }
          validationErrors[path].push(issue.message);
        }
      }
      this.formValidationErrors = validationErrors;
    }
  }

  updateFormField = (field: keyof FormModel, value: string) => {
    this.formData = { ...this.formData, [field]: value };
    this.validateForm();
  };

  updateWeeklyHours = (value: string) => {
    this.formData = { ...this.formData, weeklyHours: parseInt(value, 10) || 0 };
    this.validateForm();
  };

  handleEmployerInfoCorrectChange = (value: unknown) => {
    this.employerInfoCorrect = value === true || value === 'true';
    this.validateForm();
  };

  handleCurrentEmployeeChange = (value: boolean) => {
    const currentEmployeeValue = this.isPriorPeriodForm ? false : value;
    this.formData = { ...this.formData, currentEmployee: currentEmployeeValue };
    this.validateForm();
  };

  onFormSubmit = (_result: FormResult<FormModel>, _event?: SubmitEvent) => {
    this.preSubmit();
  };

  get normalizedStartDate() {
    return this.normalizeEmploymentDate(this.formData.employmentStartDate);
  }

  get normalizedEndDate() {
    return this.normalizeEmploymentDate(this.formData.employmentEndDate);
  }

  normalizeEmploymentDate = (date: string) => {
    if (!date) return '';
    const parsedDate = new Date(date);
    if (isNaN(parsedDate.getTime())) return '';
    // Format to YYYY-MM-DD
    const year = parsedDate.getUTCFullYear();
    const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  toggleConfirmationModal = () => {
    this.showConfirmationModal = !this.showConfirmationModal;
  };

  toggleRejectModal = () => {
    this.showRejectModal = !this.showRejectModal;
    if (!this.showRejectModal) {
      this.rejectComment = '';
      this.rejectCommentErrors = [];
    }
  };

  setRejectComment = (value: string) => {
    this.rejectComment = value;
    // Clear errors when user starts typing
    if (this.rejectCommentErrors.length > 0) {
      this.rejectCommentErrors = [];
    }
  };

  rejectForm = dropTask(async () => {
    // Validate rejection comment
    if (!this.rejectComment || this.rejectComment.trim().length === 0) {
      this.rejectCommentErrors = ['Rejection reason is required.'];
      return;
    }

    if (this.rejectComment.trim().length < 4) {
      this.rejectCommentErrors = ['Rejection reason must be at least 4 characters.'];
      return;
    }

    this.rejectCommentErrors = [];

    try {
      const form = this.args.model.form;
      // Legacy PslfFormAdapter#reject PATCHed /reject with X-Reindex-Mode: sync
      // and a { data: { type, id, attributes: { _changelog } } } body, then
      // reloaded the form; preserve both steps.
      await this.store.request(rejectPslfFormRequest(form.id!, this.rejectComment.trim()));
      // /reject goes through perform_action → `head 204` (renders NO resource),
      // so the builder does NOT refresh the cache — keep the reload via the helper.
      await reloadRecord(this.store, form);

      this.showRejectModal = false;
      this.rejectComment = '';

      // this.notifications.add('Form Successfully Rejected', {
      //   appearance: 'success',
      // });

      this.router.transitionTo('authenticated.admin.pslf.forms');
    } catch (error) {
      console.error('Failed to reject form:', error);
      this.rejectCommentErrors = ['Failed to reject form. Please try again.'];
    }
  });

  get hasExistingSignature() {
    return !!this.args.model.employee.pslfProfile.signature;
  }

  get displaySignatureUrl() {
    return this.args.model.employee.pslfProfile.signature;
  }

  get groupOptions() {
    return this.args.model.employee.company.pslfGroups.map((group: PslfGroup) => ({
      ...group,
      item: group,
      id: group.id,
      label: `${group.employerLegalName} - ${group.employerFein}`,
    }));
  }

  get usStateOptions() {
    return stateOptions.map((state) => ({
      id: state.abbr,
      label: state.name,
    }));
  }

  selectGroup = (groupId: string | null) => {
    this.selectedGroup = this.args.model.employee.company.pslfGroups.find(
      (group: PslfGroup) => group.id === groupId
    );
    if (this.hasSubmitted) {
      this.validateForm();
    }
  };

  selectManualState = (stateAbbr: string | null) => {
    this.manualEmployerData = {
      ...this.manualEmployerData,
      employerState: stateAbbr ?? '',
    };
    if (this.hasSubmitted) {
      this.validateForm();
    }
  };

  captureSignature = () => {
    const hasValidSignature =
      !!this.signatureImage || !!this.args.model.employee.pslfProfile.signature;

    if (!hasValidSignature) {
      this.signatureFieldErrors = ['Signature is required.'];
      return;
    }

    const hasSignatureComplexity = this.signaturePoints.some((group) => group.points.length > 5);

    if (this.signatureImage && !hasSignatureComplexity) {
      this.signatureFieldErrors = ['Please provide a more detailed signature.'];
      return;
    }

    if (this.signatureImage) {
      this.args.model.employee.pslfProfile.signature = this.signatureImage;
    }

    this.signatureFieldErrors = [];
    this.isOpenSignatureModal = false;
  };

  setEmployerInfoCorrect = () => {
    this.employerInfoCorrect = !this.employerInfoCorrect;
  };

  setEmploymentStartDate = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const date = selectedDates[0];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this.formData = {
        ...this.formData,
        employmentStartDate: `${year}-${month}-${day}`,
      };
      this.validateForm();
    }
  };

  setEmploymentEndDate = (selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const date = selectedDates[0];
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      this.formData = {
        ...this.formData,
        employmentEndDate: `${year}-${month}-${day}`,
      };
      this.validateForm();
    }
  };

  preSubmit = () => {
    this.hasSubmitted = true;
    this.validateForm();

    if (Object.keys(this.formValidationErrors).length > 0) {
      const firstErrorKey = Object.keys(this.formValidationErrors)[0];
      const el = document.querySelector<HTMLElement>(
        `[name='${firstErrorKey}'], [data-field='${firstErrorKey}']`
      );
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    if (!this.hasExistingSignature) {
      this.signatureFieldErrors = ['Please provide a signature before submitting.'];
      return;
    }

    this.toggleConfirmationModal();
  };

  submit = dropTask(async () => {
    const form = this.args.model.form;

    // Build base employer fields
    const employerFieldsToMerge: Record<string, unknown> = {
      'employment-start-date': this.formData.employmentStartDate,
      'employment-end-date': this.formData.employmentEndDate,
      'employment-continues': this.formData.currentEmployee,
      'employment-hours': this.formData.weeklyHours.toString(),
      'employment-status': this.formData.employmentStatus,
      'employer-contact-name': this.formData.employerContactName,
      'employer-contact-title': this.formData.employerContactTitle,
      'employer-contact-phone-number': this.formData.employerContactPhoneNumber,
      'employer-contact-email': this.formData.employerContactEmail,
      'employer-sign-date': new Date().toISOString().split('T')[0] || '',
    };

    // If employer info is not correct and using manual entry, add manual employer details
    if (!this.employerInfoCorrect && this.employerResolutionMethod === 'manualEntry') {
      employerFieldsToMerge['employer-name'] = this.manualEmployerData.employerName;
      employerFieldsToMerge['employer-fein'] = this.manualEmployerData.employerFein;
      employerFieldsToMerge['employer-street-address'] =
        this.manualEmployerData.employerStreetAddress;
      employerFieldsToMerge['employer-city'] = this.manualEmployerData.employerCity;
      employerFieldsToMerge['employer-state'] = this.manualEmployerData.employerState;
      employerFieldsToMerge['employer-postal-code'] = this.manualEmployerData.employerPostalCode;
      // Clear the unknown FEIN flag since admin has now provided the info
      employerFieldsToMerge['unknown-employer-fein'] = false;
      employerFieldsToMerge['unknownEmployerFein'] = false;
      employerFieldsToMerge['admin-manual-entry'] = true;
    }

    form.employerFields = {
      ...this.args.model.form.employerFields,
      ...employerFieldsToMerge,
    };

    // Convert kebab-case to camelCase for API
    const convertedFields: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(form.employerFields)) {
      const camelKey = key.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      convertedFields[camelKey] = value;
    }

    // @ts-expect-error: employer fields casing
    form.employerFields = convertedFields;

    const { employee } = this.args.model;

    try {
      if (employee.pslfProfile.hasDirtyAttributes) {
        await saveRecord(this.store, employee.pslfProfile);
      }

      // approverPslfProfile is assigned server-side by /sign-employer;
      // PslfFormPolicy rejects the relationship from clients (tio-api#10430).

      if (
        !this.employerInfoCorrect &&
        this.employerResolutionMethod === 'selectGroup' &&
        this.selectedGroup?.id
      ) {
        form.pslfGroup = this.selectedGroup;
      }

      // WarpDrive save helper (Phase 2.7) — replaces
      // form.save({ adapterOptions: { useV2: true } }); the legacy PslfFormAdapter
      // `useV2` update PATCHed /api/v2/pslf-forms/:id, so route the builder
      // through the `api/v2` namespace (established pattern).
      await saveRecord(this.store, form, { namespace: 'api/v2' });

      // Legacy PslfFormAdapter#signEmployer PATCHed /sign-employer with
      // X-Reindex-Mode: sync then reloaded the form; preserve both steps.
      // /sign-employer goes through perform_action → `head 204` (renders NO
      // resource), so the builder does NOT refresh the cache — keep the reload
      // via the helper.
      await this.store.request(signEmployerRequest(form.id!));
      await reloadRecord(this.store, form);

      if (this.oboPastPeriods.length > 0) {
        if (!employee.id || !form.id) {
          throw new Error('Missing employee or form ID for OBO request');
        }

        try {
          const { content: result } = (await this.store.request(
            createOboRequest({
              approverEmployeeId: employee.id,
              sourceFormId: form.id,
              pastPeriods: this.oboPastPeriods.map((period) => {
                const sourceEmployerFields = (this.args.model.form.employerFields || {}) as Record<
                  string,
                  unknown
                >;
                const contactFields = {
                  ...(sourceEmployerFields['employerContactName']
                    ? {
                        employerContactName: sourceEmployerFields['employerContactName'] as string,
                      }
                    : {}),
                  ...(sourceEmployerFields['employerContactEmail']
                    ? {
                        employerContactEmail: sourceEmployerFields[
                          'employerContactEmail'
                        ] as string,
                      }
                    : {}),
                  ...(sourceEmployerFields['employerContactTitle']
                    ? {
                        employerContactTitle: sourceEmployerFields[
                          'employerContactTitle'
                        ] as string,
                      }
                    : {}),
                  ...(sourceEmployerFields['employerContactPhoneNumber']
                    ? {
                        employerContactPhoneNumber: sourceEmployerFields[
                          'employerContactPhoneNumber'
                        ] as string,
                      }
                    : {}),
                };

                if (period.useManualEntry) {
                  return {
                    employerFields: {
                      employmentStartDate: period.employmentStartDate,
                      employmentEndDate: period.employmentEndDate,
                      employmentHours: period.employmentHours,
                      employerName: period.manualEmployerName,
                      employerFein: period.manualEmployerFein,
                      employerStreetAddress: period.manualEmployerStreetAddress,
                      employerCity: period.manualEmployerCity,
                      employerState: period.manualEmployerState,
                      employerZipCode: period.manualEmployerPostalCode,
                      ...contactFields,
                    },
                  };
                }

                return {
                  pslfGroupId: period.pslfGroup?.id ?? undefined,
                  employerFields: {
                    employmentStartDate: period.employmentStartDate,
                    employmentEndDate: period.employmentEndDate,
                    employmentHours: period.employmentHours,
                    ...contactFields,
                  },
                };
              }),
            })
          )) as { content: CreateOboResponse };

          this.notifications.add(
            this.intl.t('pslf.approver_obo.submit_success', {
              count: result.formCount,
            }),
            { appearance: 'success' }
          );
        } catch (oboError) {
          console.error('Error submitting OBO past period forms:', oboError);
          this.notifications.add(
            this.intl.t('pslf.employer_workflow.approve_success_obo_warning', {
              count: this.oboPastPeriods.length,
            }),
            { appearance: 'warning' }
          );
        }
      } else {
        this.notifications.add(this.intl.t('pslf.employer_workflow.approve_success'), {
          appearance: 'success',
        });
      }

      this.router.transitionTo('authenticated.admin.pslf.forms');
    } catch (error) {
      console.error('Error submitting PSLF form:', error);
      this.notifications.add('Something went wrong', {
        appearance: 'error',
      });
    }
  });

  enforceUSPhonePrefix(input: string): string {
    // Remove any non-numeric characters from the input
    const digits = input.replace(/\D/g, '');

    // Check if the cleaned number has 10 digits (add "1") or is an 11-digit starting with "1"
    if (digits.length === 10) {
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`;
    }

    // If the input is not 10 digits (after cleaning), or 11 digits starting with "1", it's invalid
    throw new Error('Invalid phone number. Expected 10 digits or 11 digits starting with "1".');
  }

  <template>
    <VStack>
      <Header>
        {{#if this.isPriorPeriodForm}}
          {{t "pslf.employer_workflow.partner_approver_prior_period_header"}}
        {{else}}
          {{t "pslf.employer_workflow.partner_approver_page_header"}}
        {{/if}}
      </Header>
      {{#if this.isPriorPeriodForm}}
        <AlertBanner @intent="info">
          {{t "pslf.employer_workflow.prior_period_notice"}}
        </AlertBanner>
      {{/if}}
      {{#if @model.formsAwaitingApproval.length}}
        <Section @collapsed={{true}}>
          <:header>{{t
              "pslf.employer_workflow.other_forms_awaiting_approval"
              name=this.formEmployeeName
            }}</:header>
          <:body>
            <Table @isLoading={{false}}>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "common.pslf.forms.form_type"}}</options.th>
                  <options.th>{{t "common.pslf.forms.fein"}}</options.th>
                  <options.th>{{t "common.pslf.employment_dates"}}</options.th>
                  <options.th>{{t "pslf.employer_workflow.average_hours_per_week"}}</options.th>
                  <options.th />
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each @model.formsAwaitingApproval as |form|}}
                  <options.tr>
                    <options.td>
                      {{t (concat "common.pslf.form-type." form.formTypeDisplayKey)}}
                      <br />
                      {{form.employerDisplayName}}
                    </options.td>
                    <options.td>
                      {{get form.employerFields "employer-fein"}}
                    </options.td>
                    <options.td>
                      {{safeFormatDate
                        (get form.employerFields "employment-start-date")
                        month="short"
                        day="2-digit"
                        year="numeric"
                        timeZone="UTC"
                      }}
                      {{#if (get form.employerFields "employment-end-date")}}
                        -
                        {{safeFormatDate
                          (get form.employerFields "employment-end-date")
                          month="short"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}
                      {{else if (get form.employerFields "employer-sign-date")}}
                        -
                        {{safeFormatDate
                          (get form.employerFields "employer-sign-date")
                          month="short"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}
                      {{/if}}
                    </options.td>
                    <options.td>
                      {{get form.employerFields "employment-hours"}}
                    </options.td>
                    <options.td>
                      {{#unless form.isNonPartner}}
                        <LinkTo
                          @route="authenticated.admin.pslf.form.review"
                          @model={{form.id}}
                          target="_blank"
                          class="tio-fg-info hover:tio-fg-info-body tio-inline-row"
                        >
                          <PhArrowSquareOut @size="20" />
                        </LinkTo>
                      {{/unless}}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
          </:body>
        </Section>
      {{/if}}
      {{#if @model.formsApproved.length}}
        <Section @collapsed={{false}}>
          <:header>{{t
              "pslf.employer_workflow.approved_forms"
              name=this.formEmployeeName
            }}</:header>
          <:body>
            <Table @isLoading={{false}}>
              <:thead as |options|>
                <options.tr>
                  <options.th>{{t "common.pslf.forms.employer"}}</options.th>
                  <options.th>{{t "common.pslf.forms.fein"}}</options.th>
                  <options.th>{{t "common.pslf.employment_dates"}}</options.th>
                  <options.th>{{t "pslf.employer_workflow.average_hours_per_week"}}</options.th>
                </options.tr>
              </:thead>
              <:tbody as |options|>
                {{#each this.visibleApprovedForms as |form|}}
                  <options.tr>
                    <options.td>
                      {{form.employerDisplayName}}
                    </options.td>
                    <options.td>
                      {{get form.employerFields "employer-fein"}}
                    </options.td>
                    <options.td>
                      {{safeFormatDate
                        (get form.employerFields "employment-start-date")
                        month="short"
                        day="2-digit"
                        year="numeric"
                        timeZone="UTC"
                      }}
                      {{#if (get form.employerFields "employment-end-date")}}
                        -
                        {{safeFormatDate
                          (get form.employerFields "employment-end-date")
                          month="short"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}
                      {{else if (get form.employerFields "employer-sign-date")}}
                        -
                        {{safeFormatDate
                          (get form.employerFields "employer-sign-date")
                          month="short"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}
                      {{/if}}
                    </options.td>
                    <options.td>
                      {{get form.employerFields "employment-hours"}}
                    </options.td>
                  </options.tr>
                {{/each}}
              </:tbody>
            </Table>
            {{#if (or this.hasMoreApprovedForms this.canShowLessApprovedForms)}}
              <div class="tio-flex-tight mt-2">
                {{#if this.hasMoreApprovedForms}}
                  <Button
                    @appearance="minimal"
                    @size="sm"
                    @icon={{PhCaretDown}}
                    @label={{t "common.see_more"}}
                    {{on "click" this.showMoreApprovedForms}}
                  />
                {{/if}}
                {{#if this.canShowLessApprovedForms}}
                  <Button
                    @appearance="minimal"
                    @size="sm"
                    @icon={{PhCaretUp}}
                    @label={{t "common.show_less"}}
                    {{on "click" this.showLessApprovedForms}}
                  />
                {{/if}}
              </div>
            {{/if}}
          </:body>
        </Section>
      {{/if}}
      <Section>
        <:body>
          <Form @data={{this.formData}} @onSubmit={{this.onFormSubmit}} ...attributes>
            <VStack>
              {{! EMPLOYER INFO TO VERIFY  }}
              <p class="mt-6 mb-4 tio-text-lg-semibold">
                {{#if this.isPriorPeriodForm}}
                  {{t
                    "pslf.employer_workflow.review_employer_info_prior_period"
                    name=this.formEmployeeName
                  }}
                {{else}}
                  {{t "pslf.employer_workflow.review_employer_info" name=this.formEmployeeName}}
                {{/if}}
              </p>
              <DescriptionList @cols={{2}} as |dl|>
                <dl.Group>
                  <dl.Term>{{t "pslf.workflow.employer_name"}}</dl.Term>
                  <dl.Details>
                    {{#if (get this.employerFields "employer-name")}}
                      {{get this.employerFields "employer-name"}}
                    {{else}}
                      <Text @variant="muted" @tag="span">{{t "common.not_provided"}}</Text>
                    {{/if}}
                  </dl.Details>
                </dl.Group>
                <dl.Group>
                  <dl.Term>{{t "pslf.employer_workflow.fein_header"}}</dl.Term>
                  <dl.Details>
                    {{#if (get this.employerFields "employer-fein")}}
                      {{get this.employerFields "employer-fein"}}
                    {{else}}
                      <Text @variant="muted" @tag="span">{{t "common.not_provided"}}</Text>
                    {{/if}}
                  </dl.Details>
                </dl.Group>
                <dl.Group>
                  <dl.Term>{{t "accounts.address"}}</dl.Term>
                  <dl.Details>
                    {{#if @model.form.fullEmployerAddress}}
                      {{@model.form.fullEmployerAddress}}
                    {{else}}
                      <Text @variant="muted" @tag="span">{{t "common.not_provided"}}</Text>
                    {{/if}}
                  </dl.Details>
                </dl.Group>
                {{#if (get this.employerFields "employer-website")}}
                  <dl.Group>
                    <dl.Term>{{t "pslf.workflow.employer_website"}}</dl.Term>
                    <dl.Details>{{get this.employerFields "employer-website"}}</dl.Details>
                  </dl.Group>
                {{/if}}
              </DescriptionList>
              {{#if this.hasUnknownEmployerFein}}
                <AlertBanner @intent="warning" class="mb-4">
                  {{t "pslf.admin_review.unknown_fein_notice"}}
                </AlertBanner>
              {{else}}
                <p class="font-semibold">{{t "pslf.workflow.is_employer_info_correct"}}</p>
                <RadioGroup
                  @value={{if this.employerInfoCorrect "true" "false"}}
                  @onChange={{this.handleEmployerInfoCorrectChange}}
                  @errors={{errorsForValibotField
                    "employerInfoCorrect"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                  @options={{array
                    (hash key="true" label=(t "common.yes") dataTestId="radio-yes")
                    (hash key="false" label=(t "common.no") dataTestId="radio-no")
                  }}
                />
              {{/if}}
              {{#unless this.employerInfoCorrect}}
                {{#if this.isPriorPeriodForm}}
                  <RadioGroup
                    @value={{this.employerResolutionMethod}}
                    @onChange={{this.setEmployerResolutionMethod}}
                    @options={{array
                      (hash
                        key="selectGroup"
                        label=(t "pslf.admin_review.select_from_existing")
                        dataTestId="radio-select-group"
                      )
                      (hash
                        key="manualEntry"
                        label=(t "pslf.admin_review.enter_manually")
                        dataTestId="radio-manual-entry"
                      )
                    }}
                  />
                {{/if}}

                {{#if (eq this.employerResolutionMethod "selectGroup")}}
                  {{#if this.groupOptions.length}}
                    <ReSelect
                      @label={{t "pslf.admin_review.select_correct_employer"}}
                      @items={{this.groupOptions}}
                      @selected={{if this.selectedGroup this.selectedGroup.id}}
                      @onChange={{this.selectGroup}}
                      @errors={{errorsForValibotField
                        "employerInfoCorrect"
                        schemaErrors=this.formValidationErrors
                        hasSubmitted=this.hasSubmitted
                      }}
                      data-test="select-employer-group"
                    />
                  {{else}}
                    <AlertBanner @intent="warning">
                      {{t "pslf.admin_review.no_groups_available"}}
                    </AlertBanner>
                  {{/if}}
                {{else if (eq this.employerResolutionMethod "manualEntry")}}
                  <VStack class="mt-4 p-4 border tio-surface-sunken">
                    <p class="font-semibold mb-2">{{t "pslf.admin_review.manual_entry_header"}}</p>
                    <Input
                      @name="manualEmployerName"
                      @label={{t "pslf.admin_review.employer_legal_name"}}
                      @value={{this.manualEmployerData.employerName}}
                      @onChange={{fn this.setManualEmployerField "employerName"}}
                      @isRequired={{true}}
                      @errors={{errorsForValibotField
                        "manualEmployerName"
                        schemaErrors=this.formValidationErrors
                        hasSubmitted=this.hasSubmitted
                      }}
                    />
                    <Input
                      @name="manualEmployerFein"
                      @label={{t "pslf.admin_review.employer_fein"}}
                      @value={{this.manualEmployerData.employerFein}}
                      @onChange={{fn this.setManualEmployerField "employerFein"}}
                      @isRequired={{true}}
                      placeholder="XX-XXXXXXX"
                      {{inputmask mask="99-9999999"}}
                      @errors={{errorsForValibotField
                        "manualEmployerFein"
                        schemaErrors=this.formValidationErrors
                        hasSubmitted=this.hasSubmitted
                      }}
                    />
                    <Input
                      @name="manualEmployerStreetAddress"
                      @label={{t "pslf.admin_review.street_address"}}
                      @value={{this.manualEmployerData.employerStreetAddress}}
                      @onChange={{fn this.setManualEmployerField "employerStreetAddress"}}
                      @isRequired={{true}}
                      @errors={{errorsForValibotField
                        "manualEmployerStreetAddress"
                        schemaErrors=this.formValidationErrors
                        hasSubmitted=this.hasSubmitted
                      }}
                    />
                    <HStack>
                      <Input
                        @name="manualEmployerCity"
                        @label={{t "pslf.admin_review.city"}}
                        @value={{this.manualEmployerData.employerCity}}
                        @onChange={{fn this.setManualEmployerField "employerCity"}}
                        @isRequired={{true}}
                        @errors={{errorsForValibotField
                          "manualEmployerCity"
                          schemaErrors=this.formValidationErrors
                          hasSubmitted=this.hasSubmitted
                        }}
                        class="flex-1"
                      />
                      <ReSelect
                        @label={{t "pslf.admin_review.state"}}
                        @items={{this.usStateOptions}}
                        @selected={{this.manualEmployerData.employerState}}
                        @onChange={{this.selectManualState}}
                        @errors={{errorsForValibotField
                          "manualEmployerState"
                          schemaErrors=this.formValidationErrors
                          hasSubmitted=this.hasSubmitted
                        }}
                        class="w-32"
                      />
                      <Input
                        @name="manualEmployerPostalCode"
                        @label={{t "pslf.admin_review.postal_code"}}
                        @value={{this.manualEmployerData.employerPostalCode}}
                        @onChange={{fn this.setManualEmployerField "employerPostalCode"}}
                        @isRequired={{true}}
                        placeholder="#####"
                        {{inputmask mask="99999"}}
                        @errors={{errorsForValibotField
                          "manualEmployerPostalCode"
                          schemaErrors=this.formValidationErrors
                          hasSubmitted=this.hasSubmitted
                        }}
                        class="w-32"
                      />
                    </HStack>
                  </VStack>
                {{/if}}
              {{/unless}}
            </VStack>
            <Divider class="my-9" />
            <VStack class="gap-6">
              <div>
                <Heading @level={{3}}>
                  {{#if this.isPriorPeriodForm}}
                    {{t
                      "pslf.employer_workflow.employment_status_prior_period"
                      name=this.formEmployeeName
                    }}
                  {{else}}
                    {{t
                      "pslf.employer_workflow.employment_status_current"
                      name=this.formEmployeeName
                    }}
                  {{/if}}
                </Heading>
              </div>
              <HStack class="space-x-12">
                <RadioGroup
                  @value={{this.formData.employmentStatus}}
                  @onChange={{fn this.updateFormField "employmentStatus"}}
                  @errors={{errorsForValibotField
                    "employmentStatus"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                  class="flex-none"
                  @options={{array
                    (hash key="fullTime" label=(t "common.full_time") dataTestId="radio-full-time")
                    (hash key="partTime" label=(t "common.part_time") dataTestId="radio-part-time")
                  }}
                />
                {{#unless this.isPriorPeriodForm}}
                  <Checkbox
                    @label={{t "common.pslf.forms.still_employed"}}
                    @checked={{this.formData.currentEmployee}}
                    @onChange={{this.handleCurrentEmployeeChange}}
                  />
                {{/unless}}
              </HStack>
              <div>
                <Heading @level={{4}} class="mb-1">
                  {{#if this.isPriorPeriodForm}}
                    {{t "pslf.workflow.confirm_prior_period_employment_info"}}
                  {{else}}
                    {{t "pslf.workflow.confirm_employment_info"}}
                  {{/if}}
                </Heading>
                <Text @variant="muted">
                  {{#if this.isPriorPeriodForm}}
                    {{t "pslf.workflow.info_is_from_borrower_prior_period"}}
                  {{else}}
                    {{t "pslf.workflow.info_is_from_borrower"}}
                  {{/if}}
                </Text>
              </div>
              <div>
                <label class="block font-medium tio-fg-primary mb-1">
                  {{t "pslf.workflow.start_date"}}
                </label>
                <Flatpickr
                  @date={{this.normalizedStartDate}}
                  @onChange={{this.setEmploymentStartDate}}
                  @dateFormat="Y-m-d"
                  @altInput={{true}}
                  @altFormat="F j, Y"
                  @maxDate="today"
                  class="tio-form-input"
                />
                {{#each
                  (errorsForValibotField
                    "employmentStartDate"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  )
                  as |error|
                }}
                  <p class="tio-fg-danger text-sm mt-1">{{error}}</p>
                {{/each}}
              </div>
              {{#if this.showEndDateField}}
                <div>
                  <label class="block font-medium tio-fg-primary mb-1">
                    {{t "pslf.workflow.end_date"}}
                  </label>
                  <Flatpickr
                    @date={{this.normalizedEndDate}}
                    @onChange={{this.setEmploymentEndDate}}
                    @dateFormat="Y-m-d"
                    @altInput={{true}}
                    @altFormat="F j, Y"
                    placeholder="Select end date..."
                    @minDate={{this.normalizedStartDate}}
                    @maxDate="today"
                    class="tio-form-input"
                  />
                  {{#each
                    (errorsForValibotField
                      "employmentEndDate"
                      schemaErrors=this.formValidationErrors
                      hasSubmitted=this.hasSubmitted
                    )
                    as |error|
                  }}
                    <p class="tio-fg-danger text-sm mt-1">{{error}}</p>
                  {{/each}}
                </div>
              {{/if}}
              <Input
                type="number"
                @name="weeklyHours"
                @label={{t "pslf.employer_workflow.average_hours_per_week"}}
                @value={{this.formData.weeklyHours}}
                @onChange={{this.updateWeeklyHours}}
                @errors={{errorsForValibotField
                  "weeklyHours"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                id="weekly-hours"
              />
              {{#if this.canShowOboAction}}
                <Divider class="my-9" />
                <div class="tio-flex-tight items-baseline">
                  <Heading @level={{3}}>
                    {{t "pslf.approver_obo.past_periods_header"}}
                  </Heading>
                  <Text @variant="muted" @size="sm" @tag="span" class="italic">
                    ({{t "pslf.workflow_prior_employer.optional"}})
                  </Text>
                </div>
                <VStack class="gap-2">
                  {{#unless this.oboPastPeriods.length}}
                    <Text @variant="muted" @size="sm" @tag="p">
                      {{t "pslf.approver_obo.radio_question" name=this.formEmployeeName}}
                    </Text>
                  {{/unless}}
                  <ApproverOboPastPeriodForm
                    @sourceForm={{@model.form}}
                    @approverEmployee={{@model.employee}}
                    @pastPeriods={{this.oboPastPeriods}}
                    @onPeriodsChange={{this.handleOboPeriodsChange}}
                    @employeeName={{this.formEmployeeName}}
                  />
                </VStack>
              {{/if}}
              <Divider class="my-9" />
              <VStack>
                <p class="tio-text-lg-semibold">{{t "pslf.employer_workflow.your_information"}}</p>
                <Input
                  @name="employerContactName"
                  @type="text"
                  @label="Name"
                  @value={{this.formData.employerContactName}}
                  @onChange={{fn this.updateFormField "employerContactName"}}
                  @errors={{errorsForValibotField
                    "employerContactName"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                />
                <Input
                  @name="employerContactTitle"
                  @type="text"
                  @label="Title"
                  @value={{this.formData.employerContactTitle}}
                  @onChange={{fn this.updateFormField "employerContactTitle"}}
                  @errors={{errorsForValibotField
                    "employerContactTitle"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                />
                <Input
                  @name="employerContactPhoneNumber"
                  @type="tel"
                  @label="Phone"
                  @value={{this.formData.employerContactPhoneNumber}}
                  @onChange={{fn this.updateFormField "employerContactPhoneNumber"}}
                  @errors={{errorsForValibotField
                    "employerContactPhoneNumber"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                  placeholder="+1 (###) ###-####"
                  {{inputmask mask="+1 (999) 999-9999"}}
                />
                <Input
                  @name="employerContactEmail"
                  @type="email"
                  @label="Email"
                  @value={{this.formData.employerContactEmail}}
                  @onChange={{fn this.updateFormField "employerContactEmail"}}
                  @errors={{errorsForValibotField
                    "employerContactEmail"
                    schemaErrors=this.formValidationErrors
                    hasSubmitted=this.hasSubmitted
                  }}
                />
              </VStack>
              <Divider class="my-9" />
              <VStack>
                <p class="tio-text-lg-semibold">{{t "pslf.employer_workflow.signature_section"}}</p>
                <div class="border rounded-2xl p-4 max-w-md">
                  {{#if this.hasExistingSignature}}
                    <VStack class="items-center">
                      <VStack class="items-center tio-fg-info">
                        <PhCheck @size="20" />
                        <span class="font-semibold">
                          {{#if this.signaturePoints.length}}
                            {{t "pslf.employer_workflow.signature_added"}}
                          {{else}}
                            {{t "pslf.employer_workflow.saved_signature"}}
                          {{/if}}
                        </span>
                      </VStack>
                      <img
                        src={{this.displaySignatureUrl}}
                        alt={{t "signature_preview"}}
                        class="max-w-md max-h-32"
                        data-test="signature-preview"
                      />
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        {{on "click" this.openSignatureModal}}
                        data-test="btn-change-signature"
                      >
                        {{t "pslf.employer_workflow.update_signature"}}
                      </Button>
                    </VStack>
                  {{else}}
                    <VStack class="items-center">
                      <PhPencilSimple @size="32" />
                      <p class="tio-fg-secondary font-medium">{{t
                          "pslf.employer_workflow.no_signature_yet"
                        }}</p>
                      <Button
                        @intent="primary"
                        @appearance="outlined"
                        {{on "click" this.openSignatureModal}}
                        data-test="btn-add-signature"
                      >
                        {{t "pslf.employer_workflow.add_signature"}}
                      </Button>
                    </VStack>
                  {{/if}}
                </div>
                {{#if this.signatureFieldErrors}}
                  <div class="tio-fg-danger" data-test="signature-field-errors">
                    {{#each this.signatureFieldErrors as |error|}}
                      <p>{{error}}</p>
                    {{/each}}
                  </div>
                {{/if}}
              </VStack>
              {{#if this.oboPastPeriods.length}}
                <AlertBanner @intent="warning" class="my-4">
                  {{t
                    "pslf.approver_obo.approve_alert"
                    count=this.oboPastPeriods.length
                    name=this.formEmployeeName
                  }}
                </AlertBanner>
              {{/if}}
              <div>
                <Button
                  @intent="primary"
                  {{on "click" this.preSubmit}}
                  @isRunning={{this.submit.isRunning}}
                >
                  {{t "common.approve"}}
                </Button>
                <Button
                  @intent="danger"
                  @appearance="outlined"
                  {{on "click" this.toggleRejectModal}}
                  class="ml-2"
                >
                  {{t "common.reject"}}
                </Button>
              </div>
            </VStack>

            <Modal
              @isOpen={{this.showConfirmationModal}}
              @onClose={{this.toggleConfirmationModal}}
              @size="xl"
            >
              <:header>
                {{t "pslf.employer_workflow.is_info_correct"}}
              </:header>
              <:body>
                <VStack>
                  <p>{{t "pslf.employer_workflow.please_review"}}</p>
                  <AlertBanner @intent="warning" class="my-4">
                    <p class="pt-1">
                      {{t "pslf.employer_workflow.warning" htmlSafe=true}}
                    </p>
                  </AlertBanner>
                  <DefinitionList class="px-0" as |options|>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.workflow.employer_name"
                        }}:</options.dt>
                      <options.dd>{{this.displayEmployerName}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.fein_header"
                        }}:</options.dt>
                      <options.dd>{{this.displayEmployerFein}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t "accounts.address"}}:</options.dt>
                      <options.dd>{{this.displayEmployerAddress}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "partner.employee.employee_status"
                        }}:</options.dt>
                      <options.dd>{{this.currentEmployeeText}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.workflow.start_date"
                        }}:</options.dt>
                      <options.dd>{{safeFormatDate
                          this.formData.employmentStartDate
                          month="long"
                          day="2-digit"
                          year="numeric"
                          timeZone="UTC"
                        }}</options.dd>
                    </div>
                    {{#if this.formData.employmentEndDate}}
                      <div class={{options.wrappingClass}}>
                        <options.dt class="font-semibold mr-2">{{t
                            "pslf.workflow.end_date"
                          }}:</options.dt>
                        <options.dd>{{safeFormatDate
                            this.formData.employmentEndDate
                            month="long"
                            day="2-digit"
                            year="numeric"
                            timeZone="UTC"
                          }}</options.dd>
                      </div>
                    {{/if}}
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.average_hours_per_week"
                        }}:</options.dt>
                      <options.dd>{{this.formData.weeklyHours}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.employment_type"
                        }}:</options.dt>
                      <options.dd>{{this.employmentStatusText}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.your_name"
                        }}:</options.dt>
                      <options.dd>{{this.formData.employerContactName}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.your_title"
                        }}:</options.dt>
                      <options.dd>{{this.formData.employerContactTitle}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.your_phone_number"
                        }}:</options.dt>
                      <options.dd>{{this.formData.employerContactPhoneNumber}}</options.dd>
                    </div>
                    <div class={{options.wrappingClass}}>
                      <options.dt class="font-semibold mr-2">{{t
                          "pslf.employer_workflow.your_email_address"
                        }}:</options.dt>
                      <options.dd>{{this.formData.employerContactEmail}}</options.dd>
                    </div>
                  </DefinitionList>
                  {{#if this.oboPastPeriods.length}}
                    <Divider class="my-4" />
                    <p class="tio-text-lg-semibold">
                      {{t "pslf.approver_obo.confirmation_header" name=this.formEmployeeName}}
                    </p>
                    <ul class="list-disc pl-5 space-y-1">
                      {{#each this.oboPastPeriods as |period|}}
                        <li>
                          {{t
                            "pslf.approver_obo.confirmation_period"
                            employer=(if
                              period.useManualEntry
                              period.manualEmployerName
                              (if
                                period.pslfGroup
                                (if
                                  period.pslfGroup.employerCommonName
                                  period.pslfGroup.employerCommonName
                                  period.pslfGroup.employerLegalName
                                )
                                ""
                              )
                            )
                            startDate=(safeFormatDate
                              period.employmentStartDate
                              month="long"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            )
                            endDate=(safeFormatDate
                              period.employmentEndDate
                              month="long"
                              day="2-digit"
                              year="numeric"
                              timeZone="UTC"
                            )
                            hours=period.employmentHours
                          }}
                        </li>
                      {{/each}}
                    </ul>
                    <AlertBanner @intent="warning" class="mt-2">
                      {{t "pslf.approver_obo.confirmation_warning" name=this.formEmployeeName}}
                    </AlertBanner>
                  {{/if}}
                </VStack>
              </:body>
              <:footer>
                <Footer
                  @onCancel={{this.toggleConfirmationModal}}
                  @onSubmit={{this.submit.perform}}
                  @isRunning={{this.submit.isRunning}}
                  @submitText={{t "common.submit"}}
                  @cancelText={{t "pslf.employer_workflow.edit_info"}}
                />
              </:footer>
            </Modal>

            <Modal @isOpen={{this.showRejectModal}} @onClose={{this.toggleRejectModal}}>
              <:header>
                {{t "pslf.employer_workflow.reject_form"}}
              </:header>
              <:body>
                <VStack>
                  <p>{{t "pslf.employer_workflow.reject_reason_required"}}</p>
                  <Textarea
                    @label={{t "common.reason"}}
                    @value={{this.rejectComment}}
                    @onChange={{this.setRejectComment}}
                    @isRequired={{true}}
                    placeholder={{t "pslf.employer_workflow.reject_placeholder"}}
                  />
                  {{#if this.rejectCommentErrors}}
                    <div class="tio-fg-danger" data-test="reject-comment-errors">
                      {{#each this.rejectCommentErrors as |error|}}
                        <p>{{error}}</p>
                      {{/each}}
                    </div>
                  {{/if}}
                </VStack>
              </:body>
              <:footer>
                <Footer
                  @onCancel={{this.toggleRejectModal}}
                  @onSubmit={{this.rejectForm.perform}}
                  @isRunning={{this.rejectForm.isRunning}}
                  @submitText={{t "common.reject"}}
                  @cancelText={{t "common.cancel"}}
                />
              </:footer>
            </Modal>

            <Modal
              @isOpen={{this.isOpenSignatureModal}}
              @onClose={{this.closeSignatureModal}}
              class="min-h-64"
              @size="xl"
            >
              <:header>{{t "pslf.employer_sign_page_title"}}</:header>
              <:body>
                <SignaturePad
                  @onSignatureUpdate={{this.onSignatureUpdate}}
                  @errors={{this.signatureFieldErrors}}
                />
              </:body>
              <:footer>
                <Footer
                  @isRunning={{false}}
                  @onCancel={{this.closeSignatureModal}}
                  @onSubmit={{this.captureSignature}}
                  @submitText={{t "sign"}}
                />
              </:footer>
            </Modal>
          </Form>
        </:body>
      </Section>
    </VStack>
  </template>
}
